import { paths } from '@luxuryescapes/contract-svc-lux-loyalty';

import { request } from './common';

const BASE_PATH = `${window.configs.API_HOST}/api/lux-loyalty`;

// Request + response types derived from the OpenAPI contract.

export type CustomerServiceRep =
  paths['/api/lux-loyalty/customer-service-rep']['get']['responses']['200']['content']['application/json']['result']['reps'][number];

export type CustomerServiceRepListResponse =
  paths['/api/lux-loyalty/customer-service-rep']['get']['responses']['200']['content']['application/json'];

export type CustomerServiceRepCreateBody =
  paths['/api/lux-loyalty/customer-service-rep']['post']['requestBody']['content']['application/json'];

export type CustomerServiceRepCreateResponse =
  paths['/api/lux-loyalty/customer-service-rep']['post']['responses']['201']['content']['application/json'];

export type CustomerServiceRepUpdateBody =
  paths['/api/lux-loyalty/customer-service-rep/{id}']['patch']['requestBody']['content']['application/json'];

export type CustomerServiceRepUpdateResponse =
  paths['/api/lux-loyalty/customer-service-rep/{id}']['patch']['responses']['200']['content']['application/json'];

export type CustomerServiceRepReadResponse =
  paths['/api/lux-loyalty/customer-service-rep/{id}']['get']['responses']['200']['content']['application/json'];

export type CustomerServiceRepDeleteResponse =
  paths['/api/lux-loyalty/customer-service-rep/{id}']['delete']['responses']['200']['content']['application/json'];

export type CustomerServiceRepReassignBody =
  paths['/api/lux-loyalty/customer-service-rep/reassign']['post']['requestBody']['content']['application/json'];

export type CustomerServiceRepReassignResponse =
  paths['/api/lux-loyalty/customer-service-rep/reassign']['post']['responses']['200']['content']['application/json'];

export type AccountsWithCustomerServiceRepResponse =
  paths['/api/lux-loyalty/account/customer-service-rep-mapped']['get']['responses']['200']['content']['application/json'];

export type CustomerServiceRepAssignment =
  AccountsWithCustomerServiceRepResponse['result']['accounts'][number];

interface ListReps {
  page?: number;
  pageSize?: number;
}

/** List customer service reps (admin only). */
export async function listCustomerServiceReps(params: ListReps = {}): Promise<CustomerServiceRepListResponse> {
  const query = new URLSearchParams();
  if (params.page !== undefined) {
    query.set('page', String(params.page));
  }
  if (params.pageSize !== undefined) {
    query.set('pageSize', String(params.pageSize));
  }
  const qs = query.toString();
  const uri = `${BASE_PATH}/customer-service-rep${qs ? `?${qs}` : ''}`;
  return request(uri, { method: 'GET' });
}

/** Create a customer service rep. */
export async function createCustomerServiceRep(
  body: CustomerServiceRepCreateBody,
): Promise<CustomerServiceRepCreateResponse> {
  return request(`${BASE_PATH}/customer-service-rep`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

/** Read a single customer service rep by id. */
export async function readCustomerServiceRep(id: string): Promise<CustomerServiceRepReadResponse> {
  return request(`${BASE_PATH}/customer-service-rep/${encodeURIComponent(id)}`, { method: 'GET' });
}

/** Update a customer service rep. */
export async function updateCustomerServiceRep(
  id: string,
  body: CustomerServiceRepUpdateBody,
): Promise<CustomerServiceRepUpdateResponse> {
  return request(`${BASE_PATH}/customer-service-rep/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

/** Delete a customer service rep. */
export async function deleteCustomerServiceRep(id: string): Promise<CustomerServiceRepDeleteResponse> {
  return request(`${BASE_PATH}/customer-service-rep/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/** Bulk reassign every account from one rep to another. */
export async function reassignCustomerServiceRep(
  body: CustomerServiceRepReassignBody,
): Promise<CustomerServiceRepReassignResponse> {
  return request(`${BASE_PATH}/customer-service-rep/reassign`, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  });
}

interface ListAssignments {
  customerServiceRepId?: string;
  page?: number;
  pageSize?: number;
}

/** List accounts with a customer service rep mapped (optionally filtered). */
export async function listAccountsWithCustomerServiceRep(
  params: ListAssignments = {},
): Promise<AccountsWithCustomerServiceRepResponse> {
  const query = new URLSearchParams();
  if (params.customerServiceRepId) {
    query.set('customerServiceRepId', params.customerServiceRepId);
  }
  if (params.page !== undefined) {
    query.set('page', String(params.page));
  }
  if (params.pageSize !== undefined) {
    query.set('pageSize', String(params.pageSize));
  }
  const qs = query.toString();
  const uri = `${BASE_PATH}/account/customer-service-rep-mapped${qs ? `?${qs}` : ''}`;
  return request(uri, { method: 'GET' });
}
